import { useCallback, useEffect, useState } from "react";
import {
  Account,
  AuthenticationProvider,
  AuthenticationState,
} from "./AuthenticationContext";
import { authConstants } from "../../config/auth-costants";
import { client } from "../../api/client";
import { useQueryClient } from "@tanstack/react-query";

export const DefaultAuthenticationProvider: React.FC<
  React.PropsWithChildren<{}>
> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthenticationState>("BOOTING");
  const [account, setAccount] = useState<Account | null>(null);
  const queryClient = useQueryClient();

  const handleAuthLogout = useCallback(() => {
    queryClient.clear();

    setAccount(null);
    setAuthState("SIGNED_OUT");
  }, [queryClient]);

  // The role decides which part of the dashboard the account sees
  const loadAccount = useCallback(async () => {
    const response = await client.get<Account>("/auth/me");
    setAccount(response.data);
    setAuthState("SIGNED_IN");
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem(
      authConstants.storageAccessTokenKey,
    );
    const refreshToken = localStorage.getItem(
      authConstants.storageRefreshTokenKey,
    );

    window.addEventListener(
      authConstants.refreshTokenExpiredEventKey,
      handleAuthLogout,
    );

    if (accessToken && refreshToken) {
      loadAccount().catch(handleAuthLogout);
    } else {
      setAuthState("SIGNED_OUT");
    }

    return () => {
      window.removeEventListener(
        authConstants.refreshTokenExpiredEventKey,
        handleAuthLogout,
      );
    };
  }, []);

  const login = async (
    username: string,
    password: string,
    rememberMe: boolean,
  ) => {
    const response = await client.post("/auth/sign-in", {
      username,
      password,
      rememberMe,
    });

    localStorage.setItem(
      authConstants.storageAccessTokenKey,
      response.data.accessToken,
    );
    localStorage.setItem(
      authConstants.storageRefreshTokenKey,
      response.data.refreshToken,
    );

    await loadAccount();
  };

  const signOut = async () => {
    client.post("/auth/sign-out").finally(() => {
      localStorage.removeItem(authConstants.storageAccessTokenKey);
      localStorage.removeItem(authConstants.storageRefreshTokenKey);
    });

    handleAuthLogout();
  };

  return (
    <AuthenticationProvider
      value={{
        state: authState,
        account,
        login,
        signOut,
      }}
    >
      {children}
    </AuthenticationProvider>
  );
};
